import { useRef, useEffect, useMemo, useState } from 'react';
import { loadGoogleMapsScript, googleMapsFailed, onGoogleMapsFailure } from '@/lib/googleMapsLoader';
import { useBrand } from '@/config/brands';
import { useTheme } from '@/contexts/ThemeContext';
import { MapPin, Navigation } from 'lucide-react';
import type { CarePlanData, CarePlanAssignment } from '@/hooks/useCarePlan';
import OpenStreetMapFallback from '@/components/OpenStreetMapFallback';
import { loadHospitalManifest, getHospitalInfo } from '@/lib/scenarioCoverageStore';

interface CarePlanMapProps {
  plan: CarePlanData;
  assignments: CarePlanAssignment[];
  selectedHospitalId: string | null;
  onHospitalSelect: (id: string | null) => void;
}

const AHP_STYLE: google.maps.MapTypeStyle[] = [
  { elementType: 'geometry', stylers: [{ color: '#f5f5f5' }] },
  { elementType: 'labels.text.fill', stylers: [{ color: '#616161' }] },
  { elementType: 'labels.text.stroke', stylers: [{ color: '#f5f5f5' }] },
  { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#c9c9c9' }] },
  { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#ffffff' }] },
  { featureType: 'road.highway', elementType: 'geometry', stylers: [{ color: '#dadada' }] },
  { featureType: 'poi', stylers: [{ visibility: 'off' }] },
  { featureType: 'transit', stylers: [{ visibility: 'off' }] },
];

const DARK_STYLE: google.maps.MapTypeStyle[] = [
  { elementType: 'geometry', stylers: [{ color: '#212121' }] },
  { elementType: 'labels.text.fill', stylers: [{ color: '#757575' }] },
  { elementType: 'labels.text.stroke', stylers: [{ color: '#212121' }] },
  { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#000000' }] },
  { featureType: 'road', elementType: 'geometry.fill', stylers: [{ color: '#2c2c2c' }] },
  { featureType: 'road.highway', elementType: 'geometry', stylers: [{ color: '#3c3c3c' }] },
  { featureType: 'poi', stylers: [{ visibility: 'off' }] },
  { featureType: 'transit', stylers: [{ visibility: 'off' }] },
];

// Distinct colors for up to 12 hospitals
const HOSPITAL_COLORS = [
  '#002868', '#B22234', '#16a34a', '#C5A55A', '#7c3aed',
  '#ea580c', '#0891b2', '#be185d', '#65a30d', '#dc2626',
  '#2563eb', '#d97706',
];

export function CarePlanMap({ plan, assignments, selectedHospitalId, onHospitalSelect }: CarePlanMapProps) {
  const brand = useBrand();
  const isAhp = brand.key === 'ahp';
  const { isDark } = useTheme();
  const ahpDark = isAhp && isDark;
  // Open-source (Leaflet/OSM) fallback when Google Maps auth fails. Hospital
  // coordinates come from the coverage manifest (la/lo per hospital) — no key,
  // no Supabase needed.
  const [gmFailed, setGmFailed] = useState(googleMapsFailed());
  const [manifestReady, setManifestReady] = useState(false);
  useEffect(() => onGoogleMapsFailure(() => setGmFailed(true)), []);
  useEffect(() => { if (gmFailed) loadHospitalManifest().then(() => setManifestReady(true)); }, [gmFailed]);
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<google.maps.Map | null>(null);
  const markersRef = useRef<google.maps.marker.AdvancedMarkerElement[]>([]);
  const userMarkerRef = useRef<google.maps.marker.AdvancedMarkerElement | null>(null);
  const circleRef = useRef<google.maps.Circle | null>(null);
  const [mapReady, setMapReady] = useState(false);

  // Dedupe hospitals from assignments
  const hospitals = useMemo(() => {
    const map = new Map<string, {
      id: string; name: string; city: string | null; state: string | null;
      distance: number | null; totalCost: number; procedureCount: number; colorIndex: number;
    }>();
    let idx = 0;
    for (const a of assignments) {
      if (!a.assigned_hospital_id || a.is_blended) continue;
      const existing = map.get(a.assigned_hospital_id);
      if (existing) {
        existing.totalCost += a.hospital_total_price || 0;
        existing.procedureCount += 1;
      } else {
        map.set(a.assigned_hospital_id, {
          id: a.assigned_hospital_id,
          name: a.assigned_hospital_name || 'Unknown',
          city: a.assigned_hospital_city,
          state: a.assigned_hospital_state,
          distance: a.distance_miles,
          totalCost: a.hospital_total_price || 0,
          procedureCount: 1,
          colorIndex: idx++,
        });
      }
    }
    return Array.from(map.values());
  }, [assignments]);

  // Color map for consistent hospital→color mapping
  const colorMap = useMemo(() => {
    const m = new Map<string, string>();
    hospitals.forEach(h => {
      m.set(h.id, HOSPITAL_COLORS[h.colorIndex % HOSPITAL_COLORS.length]);
    });
    return m;
  }, [hospitals]);

  // Initialize map
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;

    const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY || '';
    if (!apiKey) return;

    let cancelled = false;

    (async () => {
      await loadGoogleMapsScript(apiKey);
      await google.maps.importLibrary('marker');
      if (cancelled || !containerRef.current) return;

      const map = new google.maps.Map(containerRef.current, {
        center: { lat: Number(plan.latitude), lng: Number(plan.longitude) },
        zoom: 10,
        styles: isAhp ? (ahpDark ? DARK_STYLE : AHP_STYLE) : DARK_STYLE,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: false,
        zoomControl: true,
        zoomControlOptions: { position: google.maps.ControlPosition.RIGHT_BOTTOM },
        gestureHandling: 'greedy',
        mapId: 'care-plan-map',
      });

      mapRef.current = map;
      setMapReady(true);
    })();

    return () => { cancelled = true; };
  }, [plan.latitude, plan.longitude, isAhp]);

  // Add markers when map is ready and hospitals change
  useEffect(() => {
    if (!mapReady || !mapRef.current) return;
    const map = mapRef.current;

    // Clear old markers
    markersRef.current.forEach(m => m.map = null);
    markersRef.current = [];
    if (userMarkerRef.current) { userMarkerRef.current.map = null; userMarkerRef.current = null; }
    if (circleRef.current) { circleRef.current.setMap(null); circleRef.current = null; }

    // User location marker (star/pin)
    const userPin = document.createElement('div');
    userPin.innerHTML = `<svg width="24" height="24" viewBox="0 0 24 24" fill="none">
      <circle cx="12" cy="12" r="8" fill="${isAhp ? '#002868' : '#22c55e'}" stroke="white" stroke-width="3"/>
      <circle cx="12" cy="12" r="3" fill="white"/>
    </svg>`;
    userPin.style.cursor = 'default';

    const userMarker = new google.maps.marker.AdvancedMarkerElement({
      map,
      position: { lat: Number(plan.latitude), lng: Number(plan.longitude) },
      content: userPin,
      title: plan.location_label || 'Your Location',
      zIndex: 1000,
    });
    userMarkerRef.current = userMarker;

    // Radius circle
    const circle = new google.maps.Circle({
      map,
      center: { lat: Number(plan.latitude), lng: Number(plan.longitude) },
      radius: (plan.search_radius_miles || 50) * 1609.34, // miles to meters
      fillColor: isAhp ? '#002868' : '#22c55e',
      fillOpacity: 0.04,
      strokeColor: isAhp ? '#002868' : '#22c55e',
      strokeOpacity: 0.2,
      strokeWeight: 1,
    });
    circleRef.current = circle;

    // We need hospital lat/lng — fetch from DB via the assignment's hospital_id
    // Since we don't have lat/lng in assignments, we'll geocode from the Supabase query
    // For now, skip individual hospital markers if we don't have coords
    // The HospitalBreakdown sidebar serves as the list

    // Fit bounds to circle
    const bounds = circle.getBounds();
    if (bounds) map.fitBounds(bounds, 40);

  }, [mapReady, plan, isAhp]);

  // Fetch hospital coordinates and add markers
  useEffect(() => {
    if (!mapReady || !mapRef.current || hospitals.length === 0) return;
    const map = mapRef.current;

    // Fetch lat/lng for hospitals
    const fetchAndAddMarkers = async () => {
      const ids = hospitals.map(h => h.id);
      try {
        // Dynamic import to avoid circular deps
        const { supabase } = await import('@/integrations/supabase/client');
        const { data } = await supabase
          .from('hospitals')
          .select('id, latitude, longitude')
          .in('id', ids);

        if (!data) return;

        const coordMap = new Map(data.map(h => [h.id, { lat: Number(h.latitude), lng: Number(h.longitude) }]));
        const bounds = new google.maps.LatLngBounds();
        bounds.extend({ lat: Number(plan.latitude), lng: Number(plan.longitude) });

        // Clear old hospital markers
        markersRef.current.forEach(m => m.map = null);
        markersRef.current = [];

        for (const hosp of hospitals) {
          const coords = coordMap.get(hosp.id);
          if (!coords || !coords.lat || !coords.lng) continue;

          const color = colorMap.get(hosp.id) || '#6b7280';
          const isSelected = selectedHospitalId === hosp.id;
          const size = isSelected ? 20 : 14;

          const pin = document.createElement('div');
          pin.style.width = `${size}px`;
          pin.style.height = `${size}px`;
          pin.style.borderRadius = '50%';
          pin.style.backgroundColor = color;
          pin.style.border = `2.5px solid white`;
          pin.style.boxShadow = isSelected
            ? `0 0 0 3px ${color}, 0 2px 8px rgba(0,0,0,0.3)`
            : `0 1px 4px rgba(0,0,0,0.3)`;
          pin.style.cursor = 'pointer';
          pin.style.transition = 'all 0.2s ease';

          const marker = new google.maps.marker.AdvancedMarkerElement({
            map,
            position: coords,
            content: pin,
            title: hosp.name,
            zIndex: isSelected ? 999 : 100 + hosp.procedureCount,
          });

          marker.addListener('click', () => {
            onHospitalSelect(selectedHospitalId === hosp.id ? null : hosp.id);
          });

          markersRef.current.push(marker);
          bounds.extend(coords);
        }

        map.fitBounds(bounds, 50);
      } catch (err) {
        console.error('Failed to fetch hospital coordinates:', err);
      }
    };

    fetchAndAddMarkers();
  }, [mapReady, hospitals, colorMap, selectedHospitalId, plan.latitude, plan.longitude, onHospitalSelect]);

  // Fallback hospital pins: coords from the manifest, colored to match Google.
  const fallbackHospitals = useMemo(() => {
    if (!gmFailed || !manifestReady) return [];
    const out: Array<{ hospital_id: string; hospital_name: string; latitude: number; longitude: number; city?: string; state?: string; color?: string }> = [];
    // Plan location as an anchor pin.
    if (plan.latitude && plan.longitude) {
      out.push({ hospital_id: '__you__', hospital_name: 'Your location', latitude: Number(plan.latitude), longitude: Number(plan.longitude), color: isAhp ? '#002868' : '#22c55e' });
    }
    for (const h of hospitals) {
      const info = getHospitalInfo(h.id);
      if (info?.la != null && info?.lo != null) {
        out.push({ hospital_id: h.id, hospital_name: h.name, latitude: info.la, longitude: info.lo, city: info.c, state: info.s, color: colorMap.get(h.id), gross_charge: h.totalCost });
      }
    }
    return out;
  }, [gmFailed, manifestReady, hospitals, colorMap, plan.latitude, plan.longitude, isAhp]);

  return (
    <div className="relative w-full">
      {gmFailed ? (
        <div className="w-full h-[320px] md:h-[400px] rounded-xl overflow-hidden" style={{ minHeight: 280 }}>
          <OpenStreetMapFallback
            hospitals={fallbackHospitals}
            dark={ahpDark}
            legend={[]}
            onSelect={(id) => onHospitalSelect(id === '__you__' ? null : id)}
          />
        </div>
      ) : (
      <div
        ref={containerRef}
        className="w-full h-[320px] md:h-[400px] rounded-xl overflow-hidden"
        style={{ minHeight: 280 }}
      />
      )}
      {/* Legend overlay */}
      <div className={`absolute bottom-3 left-3 rounded-lg px-3 py-2 text-xs space-y-1 max-w-[200px] ${
        isAhp ? (ahpDark ? 'bg-[#0f1d2f]/90 text-gray-300 shadow-md' : 'bg-white/90 text-gray-700 shadow-md') : 'bg-black/80 text-white/80'
      }`}>
        <div className="flex items-center gap-1.5">
          <div className={`w-3 h-3 rounded-full border-2 border-white ${isAhp ? (ahpDark ? 'bg-[#7da6d4]' : 'bg-[#002868]') : 'bg-green-500'}`} />
          <span>Your location</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded-full bg-gray-400 border-2 border-white" />
          <span>{hospitals.length} hospitals</span>
        </div>
      </div>
    </div>
  );
}

// Export color map for other components
export { HOSPITAL_COLORS };
